import { Injectable } from '@angular/core';
import { Store, Actions, ofActionCompleted, ActionCompletion } from '@ngxs/store';
import { CarrierModel, SetInitialStateFacade } from '../../../../../../../common/models';
import {
  UliItemOverviewCarriersLoad,
  UliItemOverviewInitialState,
  UliItemOverviewLoad,
  UliItemOverviewStatusesLoad,
  UliItemOverviewUpdate,
  UliItemOverviewUpdateMainInfoDetails,
  UliItemOverviewUpdateTruckDetails,
  UliItemOverviewSapNotify,
  UliItemOverviewDeleteParkingSpot,
  UliItemOverviewUpdateParkingSpot,
} from './uli-item-overview.actions';
import { Observable } from 'rxjs';
import { UliItemOverviewState, UliItemOverviewStateModel } from './uli-item-overview.state';
import {
  UliItemActionParams,
  UliItemAllocation,
  UliItemOverviewModel,
  UliItemUliInfo,
} from '../../models/uli-item-overview.model';
import { filter, pluck, take } from 'rxjs/operators';
import { UliItemFacade } from '../uli-item.facade';
import { UliItemOverviewTruckInfoUpdateModel } from '../../models';
import { UliItemOverviewMainInfoUpdateModel } from '../../models/uli-main-info-update.model';
import { UliActivityType } from '../../../../../../../common';

@Injectable({ providedIn: 'root' })
export class UliItemOverviewFacade implements SetInitialStateFacade {
  state$: Observable<UliItemOverviewStateModel> = this.store.select(UliItemOverviewState.getState);
  overview$: Observable<UliItemOverviewModel> = this.store.select(UliItemOverviewState.getOverview);
  allocation$: Observable<UliItemAllocation> = this.store.select(UliItemOverviewState.getAllocation);
  carriers$: Observable<CarrierModel[]> = this.store.select(UliItemOverviewState.getCarriers);
  loaded$: Observable<boolean> = this.store.select(UliItemOverviewState.getLoaded);

  constructor(private store: Store, private uliItemFacade: UliItemFacade, private actions: Actions) {}

  loadAll(): void {
    this.uliItemFacade.uliIdentifiers$.pipe(filter(Boolean), take(1)).subscribe((params: UliItemActionParams) => {
      this.loadOverview(params);
      this.loadStatuses(params);
    });
  }

  setInitialState(): void {
    this.store.dispatch(new UliItemOverviewInitialState());
  }

  updateUliOverview(uliItemActionParams: UliItemActionParams, uliItem: UliItemUliInfo): void {
    this.store.dispatch(new UliItemOverviewUpdate({ uliItemActionParams, uliItem }));
  }

  updateTruckDetails(
    type: UliActivityType,
    uli: string,
    oldTruckId: number,
    toUpdate: UliItemOverviewTruckInfoUpdateModel,
  ): Observable<ActionCompletion<unknown, Error>> {
    this.uliItemFacade.uliIdentifiers$
      .pipe(filter(Boolean), take(1), pluck('compartmentId'))
      .subscribe((compartmentId: number) => {
        this.store.dispatch(
          new UliItemOverviewUpdateTruckDetails({
            compartmentId,
            type,
            uli,
            oldTruckId,
            toUpdate,
          }),
        );
      });

    return this.actions.pipe(ofActionCompleted(UliItemOverviewUpdateTruckDetails));
  }

  updateMainInfoDetails(
    compartmentId: number,
    toUpdate: UliItemOverviewMainInfoUpdateModel,
    type: UliActivityType,
    uliNumber: string,
    callback: () => void,
  ): void {
    this.store.dispatch(
      new UliItemOverviewUpdateMainInfoDetails({
        compartmentId,
        toUpdate,
        type,
        uliNumber,
        callback,
      }),
    );
  }

  deleteParkingSpot(truckId: number, hubId: number): void {
    this.store
      .dispatch(new UliItemOverviewDeleteParkingSpot({ truckId, hubId }))
      .pipe(take(1))
      .subscribe(() => this.loadAll());
  }

  updateParkingSpot(truckId: number, hubId: number, parkingSpaceId: string): void {
    this.store
      .dispatch(new UliItemOverviewUpdateParkingSpot({ truckId, hubId, parkingSpaceId }))
      .pipe(take(1))
      .subscribe(() => this.loadAll());
  }

  notifySap(params: UliItemActionParams): void {
    this.store.dispatch(new UliItemOverviewSapNotify(params));
  }

  getCarriers(searchStr: string): void {
    this.store.dispatch(new UliItemOverviewCarriersLoad(searchStr));
  }

  private loadOverview(params: UliItemActionParams): void {
    this.store.dispatch(new UliItemOverviewLoad(params));
  }

  private loadStatuses(params: UliItemActionParams): void {
    this.store.dispatch(new UliItemOverviewStatusesLoad(params));
  }
}
